import { ComponentRegistryConsumer } from '@bem-react/di';
import React, { createRef } from 'react';
import cn from 'utils/cn';

import { withFilters } from 'client/common/hooks/useFilters';
import {
    IFilter,
    ISolution,
    ISolutionSection,
    TFiltersByCategory,
    TFiltersCategory
} from 'client/common/types';
import { getDate, humanizeDate } from 'utils/date';
import i18n from 'utils/i18n';

import './index.css';

export const cnSolutionsList = cn('solutions-list');

const CATEGORIES: TFiltersCategory[] = ['industries'];

interface ISolutionsPageProps {
    filters: {
        value: TFiltersByCategory;
        add(category: TFiltersCategory, item: string): void;
    };

    title: string;
    text: string;
    solutionsList: ISolution[];
    hasMore: TFiltersByCategory;
    filterUrl: string;
    solutionsUrl: string;
    filter: Pick<ISolutionSection, TFiltersCategory>;
    section: string;
}

interface ISolutionsPageState {
    solutionsList: ISolution[];
    solutionsCount: number;
    more: boolean;
    hasMore: TFiltersByCategory;
    loading: boolean;
    loadingError: boolean;
}

class SolutionsPage extends
    React.Component<ISolutionsPageProps, ISolutionsPageState> {

    state: ISolutionsPageState = {
        solutionsList: this.props.solutionsList,
        solutionsCount: this.props.solutionsList.length,
        more: true,
        hasMore: this.props.hasMore,
        loading: false,
        loadingError: false
    };

    private itemsRef = createRef<HTMLDivElement>();

    private chunkLength = 8;

    componentDidMount(): void {
        window.addEventListener('scroll', this.onScroll);
    }

    componentDidUpdate(prevProps: Readonly<ISolutionsPageProps>) {
        if (this.props.filters.value !== prevProps.filters.value) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                solutionsList: [],
                solutionsCount: 0,
                more: true,
                loading: true
            }, this.loadSolutionsChunk);
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('scroll', this.onScroll);
    }

    render() {
        const { filters, title, text, filter, solutionsUrl } = this.props;
        const { hasMore, loading, loadingError, solutionsList } = this.state;

        const addHandler = (category: TFiltersCategory, item: string) => () => {
            const categoryFilters = filters.value[category] || [];

            if (!categoryFilters.includes(item)) {
                filters.add(category, item);
            }
        };

        return (
            <div className={cnSolutionsList()}>
                <div className={cnSolutionsList('filters')}>
                    <ComponentRegistryConsumer id="app">{({ Level }) => (
                        <Level
                            type="filters"
                            groups={filter}
                            hasMore={hasMore}
                            order={['tasks', 'industries', 'products', 'companySize']}
                            title={title}
                            text={text}
                            />
                    )}
                    </ComponentRegistryConsumer>
                </div>
                <div ref={this.itemsRef} className={cnSolutionsList('items')}>
                    {solutionsList && solutionsList.map(
                        // eslint-disable-next-line max-statements
                        ({
                            // eslint-disable-next-line no-shadow
                            title,
                            slug,
                            theme,
                            date: dateString,
                            listImage,
                            listImageView,
                            important,
                            cardType,
                            ...solution
                        }) => {
                            const tags: React.ReactNodeArray = [];

                            const { companySize } = solution;

                            const date = getDate(dateString) as Date;
                            const humanizedDate = humanizeDate(date, 'full');

                            // TODO: Вынести в функцию для сбора тегов
                            CATEGORIES.forEach(category =>
                                tags.push(
                                    ...solution[category]
                                        .split(',')
                                        // eslint-disable-next-line max-nested-callbacks
                                        .map(categorySlug =>
                                            filter[category]
                                            // eslint-disable-next-line max-nested-callbacks
                                                .find((el: IFilter) =>
                                                    el && el.slug === categorySlug))
                                        .map((foundedFilter: IFilter | undefined) => (
                                            foundedFilter && (
                                                <span
                                                    onClick={addHandler(
                                                        category,
                                                        foundedFilter.slug
                                                    )}
                                                    key={foundedFilter.slug}
                                                    className={cnSolutionsList('card-tag')}
                                                    >{foundedFilter.name}
                                                </span>
                                            ))
                                        )
                                )
                            );

                            const cardMods: { [key: string]: string | boolean } = {};

                            if (listImage) {
                                cardMods.image = 'yes';
                            }

                            cardMods.imageView = listImageView;
                            cardMods.important = important;
                            cardMods.type = cardType;

                            const style = listImage
                                ? { backgroundImage: `url(${listImage})` }
                                : {};

                            return (
                                <div
                                    key={title}
                                    className={cnSolutionsList('card', cardMods)}
                                    style={style}
                                    >
                                    <div className={cnSolutionsList('card-tags')}>
                                        {tags}
                                    </div>
                                    {theme && (
                                        <div
                                            className={cnSolutionsList(
                                                'card-theme',
                                                { companySize })}
                                            dangerouslySetInnerHTML={{ __html: theme }}
                                            />
                                    )}
                                    { /* TODO: прокидывать секцию из бункера */ }
                                    <h2
                                        className={cnSolutionsList('card-title')}
                                        >
                                        <a
                                            href={`${solutionsUrl}/cases/${slug}`}
                                            dangerouslySetInnerHTML={{ __html: title }}
                                            />
                                    </h2>
                                    <div className={cnSolutionsList('card-date')}>
                                        <time
                                            dateTime={date.toISOString()}
                                            itemProp="dateModified"
                                            >
                                            {humanizedDate}
                                        </time>
                                    </div>
                                </div>);
                        })}
                </div>
                <div className={cnSolutionsList('spin')}>
                    <ComponentRegistryConsumer id="lego">{({ Spin }) => loadingError ? (
                        <div className={cnSolutionsList('loading-error')}>
                            {i18n({ keyset: 'common', key: 'loading-error' })}
                        </div>
                    ) : (
                        <Spin
                            progress={loading}
                            view="default"
                            tone="default"
                            size="m"
                            position="center"
                            />
                    )}
                    </ComponentRegistryConsumer>
                </div>
            </div>
        );
    }

    private getItemsTopOffset = () =>
        this.itemsRef.current!.getBoundingClientRect().top + window.pageYOffset

    private getItemsHeight = () => this.itemsRef.current!.clientHeight;

    private onScroll = () => {
        const windowOffset = window.pageYOffset + window.innerHeight;
        const contentOffset = this.getItemsTopOffset() + this.getItemsHeight();

        const areManySolutions = this.state.solutionsCount >= this.chunkLength;

        const { loading, more } = this.state;

        if (!loading && more && areManySolutions && windowOffset > contentOffset) {
            this.setState({ loading: true }, this.loadSolutionsChunk);
        }
    }

    private loadSolutionsChunk = () => {
        const { filters, filterUrl, section } = this.props;

        fetch(filterUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                startIdx: this.state.solutionsCount,
                filter: Object.keys(filters.value)
                    .map((category: TFiltersCategory) => ({
                        by: category,
                        value: filters.value[category]
                    }))
                    .filter(item => item.value),
                section
            })
        })
            .then(res => res.json())
            .then(data => {
                if (!data || !Array.isArray(data.list)) {
                    return this.setState({
                        loadingError: true
                    });
                }

                this.setState(prevState => ({
                    solutionsList: prevState.solutionsList.concat(data.list),
                    solutionsCount: prevState.solutionsCount + data.list.length,
                    more: data.more,
                    hasMore: data.hasMore
                }));
            })
            .then(() => this.setState({ loading: false }))
            .catch(() => this.setState({ loadingError: true }));
    }
}

// TODO: Переписать SolutionsPage на хуки и удалить функцию withFilters
// tslint:disable-next-line
export default withFilters(SolutionsPage as any);
